import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  pointerWithin,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Waypoint } from "@/api.types";
import {
  useArchiveTrip,
  useAssignOperatorToTrip,
  useDeleteTripWaypoint,
  useTripById,
  useTripWaypointsReorder,
  useDeleteTrip,
  useComputeRoute,
} from "@/api/trips";
import { useOperators } from "@/api/operators";
import { useTripsStore } from "@/screens/dashboard/trip-temp-order.store";
import {
  MapPin,
  Users,
  ListOrdered,
  Route,
  User,
  Loader2,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import { RouteSettingsDialog } from "./route-settings-dialog";
import { PickupOperatorsDialog } from "./pickup-operators-dialog";

function WaypointItem({
  waypoint,
  onClick,
}: {
  waypoint: Waypoint;
  onClick?: (waypoint: Waypoint) => void;
}) {
  const temporaryWaypointOrder = useTripsStore(
    (state) => state.temporaryWaypointOrder
  );
  const formattedAddress = waypoint.tasks[0]?.address
    ? `${waypoint.tasks[0]?.address?.line1}`
    : "No address available";

  const completedTasksCount = waypoint.tasks.filter((task) =>
    ["COMPLETED", "FAILED", "CANCELLED"].includes(task.status)
  ).length;
  const totalTasks = waypoint.tasks.length;
  const completionPercentage = (completedTasksCount / totalTasks) * 100;

  // Check if there's a temporary sequence for this waypoint
  const hasTemporarySequence = temporaryWaypointOrder?.[waypoint.id];
  const displaySequence = hasTemporarySequence
    ? temporaryWaypointOrder[waypoint.id]
    : waypoint.sequence;

  // Get order numbers for display
  const orderNumbers = waypoint.tasks
    .map((task) => task.order.orderNumber)
    .filter((value, index, self) => self.indexOf(value) === index);

  // Format ETA time if available
  const formatETA = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Use real ETA data if available, otherwise show N/A
  const estimatedArrival = formatETA(waypoint.eta?.estimatedArrivalTime);
  const actualArrival = formatETA(waypoint.eta?.actualArrivalTime);

  // Calculate ETA range if duration is available
  let etaDisplay = estimatedArrival;
  let actualArrivalDisplay = actualArrival;
  if (estimatedArrival !== "N/A") {
    const arrivalTime = new Date(waypoint.eta?.estimatedArrivalTime ?? "");

    // Create a range with the arrival time and +10 minutes
    const laterTime = new Date(arrivalTime.getTime() + 10 * 60 * 1000);
    const laterTimeFormatted = formatETA(laterTime.toISOString());

    // Display as original time - later time
    etaDisplay = `${estimatedArrival} - ${laterTimeFormatted}`;
  }
  if (actualArrival !== "N/A") {
    actualArrivalDisplay = actualArrival;
    const arrivalTime = new Date(waypoint.eta?.actualArrivalTime ?? "");

    // Create a range with the arrival time and +10 minutes
    const laterTime = new Date(arrivalTime.getTime() + 10 * 60 * 1000);
    const laterTimeFormatted = formatETA(laterTime.toISOString());

    // Display as original time - later time
    actualArrivalDisplay = `${actualArrival} - ${laterTimeFormatted}`;
  }

  // Count pickup and delivery tasks
  const pickupCount = waypoint.tasks.filter(
    (task) => task.type === "PICKUP"
  ).length;
  const deliveryCount = waypoint.tasks.filter(
    (task) => task.type === "DELIVERY"
  ).length;

  // Internal handler to stop propagation
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event from bubbling to drag listeners
    onClick?.(waypoint);
  };

  return (
    <div
      className="rounded mb-2 cursor-pointer hover:opacity-90 transition-opacity"
      onClick={handleClick} // Use the internal handler
    >
      <div className="flex items-center">
        <div className="flex flex-col">
          {/* Add completion percentage above sequence number */}
          {!hasTemporarySequence && (
            <div className="h-2 bg-gray-500 w-10 rounded-t-lg overflow-hidden">
              <div
                className="h-full bg-[#4CAF50]"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          )}

          {/* Sequence number */}
          <div
            className={`relative w-10 h-10 flex items-center justify-center ${hasTemporarySequence ? "rounded-t-lg" : ""} overflow-hidden`}
            style={{
              backgroundColor: hasTemporarySequence
                ? "red"
                : (waypoint.trip?.color ?? "gray"),
            }}
          >
            <span className="relative text-lg font-medium z-10 text-white">
              {displaySequence}
            </span>
          </div>

          {/* Only show pickup count if greater than 0 */}
          {pickupCount > 0 && (
            <div
              className={`text-sm text-white bg-blue-600 text-center ${
                deliveryCount === 0 ? "rounded-b-lg" : ""
              }`}
            >
              P-{pickupCount}
            </div>
          )}
          {/* Only show delivery count if greater than 0 */}
          {deliveryCount > 0 && (
            <div
              className={`text-sm text-white bg-purple-400 text-center rounded-b-lg`}
            >
              D-{deliveryCount}
            </div>
          )}
        </div>

        <div className="flex-1 rounded-lg p-3 bg-white shadow-sm">
          {/* ETA section as a banner at the top */}
          <div className="mb-3">
            <span className="block text-sm font-semibold text-blue-700 bg-blue-50 px-2 py-1 rounded text-center">
              ETA: {etaDisplay}
            </span>
          </div>
          {actualArrival !== "N/A" && (
            <div className="mb-3">
              <span className="block text-sm font-semibold text-orange-700 bg-orange-50 px-2 py-1 rounded text-center">
                Actual Arrival: {actualArrivalDisplay}
              </span>
            </div>
          )}

          <div className="font-medium text-lg  mb-2">{formattedAddress}</div>

          {/* Orders section */}
          <div className="mt-2">
            <div className="flex items-center gap-2">
              <div className="flex flex-wrap gap-1">
                {orderNumbers.length > 0 ? (
                  orderNumbers.map((orderNum, index) => {
                    // Find all tasks related to this order number
                    const orderTasks = waypoint.tasks.filter(
                      (task) => task.order.orderNumber === orderNum
                    );
                    const orderCompletedTasks = orderTasks.filter((task) =>
                      ["COMPLETED", "FAILED", "CANCELLED"].includes(task.status)
                    );
                    const isOrderComplete =
                      orderCompletedTasks.length === orderTasks.length;
                    const isOrderPartiallyComplete =
                      orderCompletedTasks.length > 0 &&
                      orderCompletedTasks.length < orderTasks.length;

                    // Determine background color based on completion status
                    let bgColor = "bg-gray-100"; // Default
                    if (isOrderComplete) {
                      // Check for specific completion statuses
                      const allCancelled = orderTasks.every(
                        (task) => task.status === "CANCELLED"
                      );
                      const anyFailed = orderTasks.some(
                        (task) => task.status === "FAILED"
                      );

                      if (allCancelled) bgColor = "bg-gray-200";
                      else if (anyFailed) bgColor = "bg-red-100";
                      else bgColor = "bg-green-100";
                    } else if (isOrderPartiallyComplete) {
                      bgColor = "bg-yellow-100";
                    }

                    return (
                      <span
                        key={index}
                        className={`inline-block ${bgColor} px-2 py-0.5 rounded text-sm font-medium flex items-center`}
                        title={`${orderCompletedTasks.length}/${orderTasks.length} tasks completed`}
                      >
                        <span>#{orderNum}</span>
                        {isOrderComplete && (
                          <>
                            {orderTasks.every(
                              (task) => task.status === "CANCELLED"
                            ) && <span className="ml-1 text-gray-600">✕</span>}
                            {orderTasks.some(
                              (task) => task.status === "FAILED"
                            ) && <span className="ml-1 text-red-600">✗</span>}
                            {orderTasks.every(
                              (task) => task.status === "COMPLETED"
                            ) && <span className="ml-1 text-green-600">✓</span>}
                          </>
                        )}
                        {isOrderPartiallyComplete && (
                          <span className="ml-1 text-yellow-600">⟳</span>
                        )}
                      </span>
                    );
                  })
                ) : (
                  <span className="text-sm text-gray-400">No orders</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// New component for reordering view
function ReorderWaypointItem({
  waypoint,
  displaySequence,
}: {
  waypoint: Waypoint;
  displaySequence: number;
}) {
  const temporaryWaypointOrder = useTripsStore(
    (state) => state.temporaryWaypointOrder
  );
  const hasTemporarySequence = temporaryWaypointOrder?.[waypoint.id];

  return (
    <div
      className="w-10 h-10 flex items-center justify-center rounded-lg"
      style={{
        backgroundColor: hasTemporarySequence
          ? "red"
          : (waypoint.trip?.color ?? "gray"),
      }}
    >
      <span className="text-lg font-medium text-white">{displaySequence}</span>
    </div>
  );
}

interface SortableWaypointProps {
  waypoint: Waypoint;
  onWaypointSelect?: (waypoint: Waypoint) => void;
  isReordering: boolean;
}

function SortableWaypoint({
  waypoint,
  onWaypointSelect,
  isReordering,
}: SortableWaypointProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: waypoint.id });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
  };

  const temporaryWaypointOrder = useTripsStore(
    (state) => state.temporaryWaypointOrder
  );
  const displaySequence =
    temporaryWaypointOrder?.[waypoint.id] ?? waypoint.sequence;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {isReordering ? (
        <ReorderWaypointItem
          waypoint={waypoint}
          displaySequence={displaySequence}
        />
      ) : (
        <WaypointItem waypoint={waypoint} onClick={onWaypointSelect} />
      )}
    </div>
  );
}

import { RouteStartTimeDialog } from "./route-start-time-dialog";

export function TripWaypointsList({
  tripId,
  onTemporaryOrderChange,
  onWaypointSelect,
}: {
  tripId: string;
  onTemporaryOrderChange?: (order: Record<string, number> | undefined) => void;
  onWaypointSelect?: (waypoint: Waypoint | null) => void;
}) {
  const { toast } = useToast();
  const [isRouteSettingsOpen, setIsRouteSettingsOpen] = useState(false);
  const [isPickupOperatorsOpen, setIsPickupOperatorsOpen] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isStartTimeDialogOpen, setIsStartTimeDialogOpen] = useState(false);
  const [waypointToDelete, setWaypointToDelete] = useState<Waypoint | null>(
    null
  );

  // Add query to fetch trip details
  const { data: tripDetails, isLoading: isLoadingTrip } = useTripById(tripId);
  const waypoints = useMemo(() => tripDetails?.waypoints ?? [], [tripDetails]);

  // Remove the query since we're now receiving waypoints as a prop
  const [localWaypoints, setLocalWaypoints] = useState<Waypoint[]>(waypoints);
  const [isOrderChanged, setIsOrderChanged] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Update local waypoints when prop waypoints change
  useEffect(() => {
    setLocalWaypoints(waypoints);

    // Process waypoints to include ETA data from tripDetails
    if (tripDetails && waypoints) {
      const updatedWaypoints = waypoints.map((waypoint) => {
        // Find the corresponding waypoint in tripDetails
        const tripWaypoint = tripDetails.waypoints?.find(
          (w) => w.id === waypoint.id
        );

        // If found and it has eta, attach it to our waypoint
        if (tripWaypoint?.eta) {
          return {
            ...waypoint,
            eta: tripWaypoint.eta,
          };
        }

        return waypoint;
      });

      setLocalWaypoints(updatedWaypoints);
    }
  }, [waypoints, tripDetails]);

  const mutation = useTripWaypointsReorder(tripId, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Waypoints reordered successfully",
        variant: "default",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save waypoint order",
        variant: "destructive",
      });
    },
  });

  const assignOperatorMutation = useAssignOperatorToTrip(tripId, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Operator assigned successfully",
        variant: "default",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to assign operator",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useDeleteTripWaypoint(tripId, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Waypoint deleted successfully",
        variant: "default",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete waypoint",
        variant: "destructive",
      });
    },
  });

  const archiveTripMutation = useArchiveTrip(tripId, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Trip archived successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to archive trip",
        variant: "destructive",
      });
    },
  });

  const computeRouteMutation = useComputeRoute({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Route computed successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to compute route",
        variant: "destructive",
      });
    },
  });

  const deleteTrip = useDeleteTrip();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // Increase the distance threshold to prevent accidental drags
        distance: isReordering ? 15 : 8,
        // Add a delay even when not reordering to allow clicks
        delay: isReordering ? 150 : 200,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    // If not dropped over the list and in reordering mode, check if it's far enough from the container
    if (!over && isReordering) {
      // Get the container element's bounds
      const containerElement = document.querySelector(".droppable-container");
      if (containerElement) {
        // const containerRect = containerElement.getBoundingClientRect();
        const dragDistance = Math.min(
          Math.abs(event.delta.x),
          Math.abs(event.delta.y)
        );

        // Only trigger delete if dragged significantly away from the container
        const deleteThreshold = 200; // pixels
        if (dragDistance > deleteThreshold) {
          const waypoint = localWaypoints.find((w) => w.id === active.id);
          if (waypoint) {
            setWaypointToDelete(waypoint);
            setIsDeleteDialogOpen(true);
          }
        }
      }
      return;
    } else if (!over && !isReordering) {
      // In normal mode, show delete confirmation immediately
      const waypoint = localWaypoints.find((w) => w.id === active.id);
      if (waypoint) {
        setWaypointToDelete(waypoint);
        setIsDeleteDialogOpen(true);
      }
      return;
    }

    // Handle normal reordering
    if (active.id !== over?.id) {
      const oldIndex = localWaypoints.findIndex(
        (waypoint) => waypoint.id === active.id
      );
      const newIndex = localWaypoints.findIndex(
        (waypoint) => waypoint.id === over?.id
      );
      const newWaypoints = arrayMove(localWaypoints, oldIndex, newIndex);
      setLocalWaypoints(newWaypoints);
      setIsOrderChanged(true);

      const tempOrder = newWaypoints.reduce(
        (acc, waypoint, index) => {
          acc[waypoint.id] = index + 1;
          return acc;
        },
        {} as Record<string, number>
      );

      onTemporaryOrderChange?.(tempOrder);
    }
  };

  const handleDeleteConfirm = () => {
    if (waypointToDelete) {
      deleteMutation.mutate(waypointToDelete.id);
      // Update local state
      setLocalWaypoints((prev) =>
        prev.filter((w) => w.id !== waypointToDelete.id)
      );
      setIsDeleteDialogOpen(false);
      setWaypointToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setWaypointToDelete(null);
  };

  const handleSaveOrder = () => {
    const waypointOrder = localWaypoints.map((waypoint, index) => ({
      waypointId: waypoint.id,
      sequence: index + 1,
    }));
    mutation.mutate(waypointOrder);
    setIsOrderChanged(false);
    onTemporaryOrderChange?.(undefined);
    setIsReordering(false);
  };

  const handleCancelOrder = () => {
    setLocalWaypoints(waypoints);
    setIsOrderChanged(false);
    onTemporaryOrderChange?.(undefined);
    setIsReordering(false);
  };

  const handleAssignOperator = (operatorId: string) => {
    assignOperatorMutation.mutate(operatorId);
  };

  const handleWaypointSelect = (waypoint: Waypoint) => {
    if (onWaypointSelect) {
      // We don't need to modify the waypoint, as the parent component can use the tripId
      // from the TripWaypointsList props to set the correct tripId in the store
      onWaypointSelect(waypoint);
    }
  };

  const handleArchiveTrip = () => {
    archiveTripMutation.mutate();
  };

  const handleComputeRoute = () => {
    // Open the start time dialog instead of immediately computing
    setIsStartTimeDialogOpen(true);
  };

  const handleStartTimeSubmit = (startTime: Date) => {
    // Format the date as ISO string to preserve timezone information
    const customStartTime = startTime.toISOString();

    // Call the mutation with both tripId and customStartTime
    computeRouteMutation.mutate({
      tripId,
      customStartTime,
    });

    toast({
      title: "Computing route",
      description: `Route will be computed with start time: ${format(startTime, "PPp")}`,
    });
  };

  const { data: operatorsResponse, isLoading: isLoadingOperators } =
    useOperators({
      page: 1,
      limit: 1000,
      paginationType: "offset",
      sortBy: "createdAt",
      sortOrder: "desc",
      status: "ON_DUTY,OFF_DUTY",
    });
  const operators = operatorsResponse?.data;

  // Add function to handle trip deletion
  const handleDeleteTrip = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("Are you sure you want to delete this trip?")) {
      deleteTrip.mutate(tripId);
    }
  };

  // Show loading indicator for the whole component if the trip is loading
  if (isLoadingTrip) {
    return (
      <div className="flex justify-center items-center min-h-[200px] bg-gray-100 py-2 rounded-lg">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="bg-gray-100 py-2 rounded-lg">
      <div className="flex mb-2 justify-between items-center px-2">
        <div className="flex items-center gap-1">
          <Select
            onValueChange={handleAssignOperator}
            value={tripDetails?.operatorId ?? undefined}
          >
            <SelectTrigger className="w-[140px] focus:ring-0 focus:ring-offset-0 h-9">
              <User className="h-4 w-4 mr-1" />
              <SelectValue placeholder="Assign" />
            </SelectTrigger>
            <SelectContent>
              {isLoadingOperators ? (
                <div className="flex justify-center items-center p-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                operators?.map((operator) => (
                  <SelectItem key={operator.id} value={operator.id}>
                    {operator.user.firstName} {operator.user.lastName}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={handleComputeRoute}
            disabled={computeRouteMutation.isPending}
            className="flex items-center gap-1 w-9 h-9 p-0"
            title="Compute Route"
          >
            {computeRouteMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Route className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant={isReordering ? "secondary" : "outline"}
            size="sm"
            onClick={() => setIsReordering((prev) => !prev)}
            className="flex items-center gap-1 w-9 h-9 p-0"
            title={isReordering ? "Exit Reorder Mode" : "Reorder Waypoints"}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPickupOperatorsOpen(true)}
            className="flex items-center gap-1 w-9 h-9 p-0"
            title="Pickup Operators"
          >
            <Users className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsRouteSettingsOpen(true)}
            className="flex items-center gap-1 w-9 h-9 p-0"
            title="Route Settings"
          >
            <MapPin className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDeleteTrip}
            className="flex items-center gap-1 w-9 h-9 p-0"
            title="Delete Trip"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <RouteStartTimeDialog
        isOpen={isStartTimeDialogOpen}
        onOpenChange={setIsStartTimeDialogOpen}
        onSubmit={handleStartTimeSubmit}
      />

      <RouteSettingsDialog
        tripId={tripId}
        isOpen={isRouteSettingsOpen}
        onOpenChange={setIsRouteSettingsOpen}
      />

      <PickupOperatorsDialog
        tripId={tripId}
        isOpen={isPickupOperatorsOpen}
        onOpenChange={setIsPickupOperatorsOpen}
      />

      {localWaypoints.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          No waypoints found for this trip.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div
            className={` droppable-container ${
              isReordering
                ? "flex flex-wrap gap-4 min-h-[200px] bg-gray-50 rounded-lg p-8 border-2 border-dashed border-gray-200"
                : "min-h-[200px] px-2"
            }`}
          >
            <SortableContext
              items={localWaypoints.map((waypoint) => waypoint.id)}
              strategy={
                isReordering ? rectSortingStrategy : verticalListSortingStrategy
              }
            >
              {localWaypoints.map((waypoint) => (
                <SortableWaypoint
                  key={waypoint.id}
                  waypoint={waypoint}
                  onWaypointSelect={handleWaypointSelect}
                  isReordering={isReordering}
                />
              ))}
            </SortableContext>
          </div>

          <DragOverlay>
            {activeId
              ? (() => {
                  const activeWaypoint = localWaypoints.find(
                    (w) => w.id === activeId
                  );
                  if (!activeWaypoint) return null;

                  const temporaryWaypointOrder =
                    useTripsStore.getState().temporaryWaypointOrder;
                  const displaySequence =
                    temporaryWaypointOrder?.[activeWaypoint.id] ??
                    activeWaypoint.sequence;

                  return (
                    <div className="opacity-50">
                      {isReordering ? (
                        <ReorderWaypointItem
                          waypoint={activeWaypoint}
                          displaySequence={displaySequence}
                        />
                      ) : (
                        <WaypointItem
                          waypoint={activeWaypoint}
                          onClick={handleWaypointSelect}
                        />
                      )}
                    </div>
                  );
                })()
              : null}
          </DragOverlay>
        </DndContext>
      )}

      <div className="flex flex-row justify-between mt-4 px-2">
        <div className="">
          {tripDetails?.status === "COMPLETED" && (
            <Button
              disabled={tripDetails?.status !== "COMPLETED"}
              size="sm"
              onClick={handleArchiveTrip}
              variant={"outline"}
            >
              Archive Trip
            </Button>
          )}
        </div>
        {(isOrderChanged || isReordering) && (
          <div className="flex flex-row gap-2">
            <Button
              size="sm"
              onClick={handleCancelOrder}
              variant="outline"
              disabled={!isOrderChanged && !isReordering}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSaveOrder}
              variant={isOrderChanged ? "default" : "outline"}
              disabled={!isOrderChanged}
            >
              Save Sequence
            </Button>
          </div>
        )}
      </div>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this waypoint?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
